import React from 'react'
import { useForm } from 'react-hook-form'
import type {
  DefaultValues,
  FieldValues,
  SubmitHandler,
  UseFormProps,
} from 'react-hook-form'

const ROOT_API_ERROR = 'root.apiError'

export const useFormApiError = (state, onApiError) => {
  const { apiError } = state

  React.useEffect(() => {
    if (apiError != null) {
      onApiError(apiError)
    }
  }, [apiError, onApiError])
}

export const useFormReset = (state, onFormReset) => {
  const { shouldReset } = state

  React.useEffect(() => {
    if (shouldReset) {
      onFormReset()
    }
  }, [onFormReset, shouldReset])
}

// Generic over the form's field values so a precisely-typed resolver -
// `yupResolver(schema)` returns `Resolver<{ email: string }>`, not
// `Resolver<FieldValues>` - stays assignable. Defaulting the parameter to
// `FieldValues` keeps every existing untyped caller working unchanged.
type UseControlledFormOptions<TFieldValues extends FieldValues> = Pick<
  UseFormProps<TFieldValues>,
  'mode' | 'resolver' | 'reValidateMode' | 'values'
> & {
  // Narrower than `UseFormProps['defaultValues']`, which also allows an async
  // loader returning a promise. `handleFormReset` feeds this straight to
  // `reset()`, which takes no promise, so allowing one here would type-check
  // into a runtime failure. No caller uses the async form.
  defaultValues?: DefaultValues<TFieldValues>
  handleFormData: SubmitHandler<TFieldValues>
}

export const useControlledForm = <
  TFieldValues extends FieldValues = FieldValues,
>({
  defaultValues,
  mode = 'onSubmit',
  resolver,
  reValidateMode = 'onChange',
  values,
  handleFormData,
}: UseControlledFormOptions<TFieldValues>) => {
  const {
    control,
    formState,
    getValues,
    handleSubmit,
    reset,
    setError,
    setValue,
    watch,
    trigger,
  } = useForm<TFieldValues>({
    defaultValues,
    mode,
    resolver,
    reValidateMode,
    values,
  })

  const isDirtyOrInvalid =
    formState.isDirty || Object.keys(formState.errors).length !== 0

  const handleFormApiError = React.useCallback(
    ({ message, type }) => setError(ROOT_API_ERROR, { type, message }),
    [setError],
  )

  const handleFormReset = React.useCallback(
    (options = {}) => {
      reset(defaultValues, { ...options })
    },
    [defaultValues, reset],
  )

  return {
    control,
    formState,
    getValues,
    handleFormApiError,
    handleFormReset,
    handleFormSubmit: handleSubmit(handleFormData),
    setValue,
    watch,
    trigger,
    isDirtyOrInvalid,
  }
}
